import { useEffect, useRef, useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { api, ApiError, type BrowserLog, type SupportKind } from '@/lib/api';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui';

/**
 * Support pill — the small "?" in the bottom-right corner, and the ticket form
 * behind it. Ported from the task app's FeedbackWidget.
 *
 * `signedOut` renders the login-issue variant used on the login page: it asks
 * for an email address (the only way to reply to someone who can't get in) and
 * offers only the kinds the server accepts without a session.
 */

/** Console noise that says nothing about a user's problem. */
const IGNORED = [/^\[vite\]/i, /Download the React DevTools/i, /React Router Future Flag/i];

const KINDS = (): { value: SupportKind; label: string }[] => [
  { value: 'issue', label: t('supportPill.kindIssue') },
  { value: 'question', label: t('supportPill.kindQuestion') },
  { value: 'feedback', label: t('supportPill.kindFeedback') },
];

export function SupportPill({ signedOut = false }: { signedOut?: boolean }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<SupportKind>(signedOut ? 'login_issue' : 'issue');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raised, setRaised] = useState<{ id: number; deskRef: string | null } | null>(null);

  const logs = useCapturedLogs();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape, and move focus into the panel when it opens so the form is
  // reachable from the keyboard without hunting for it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLElement>('select, textarea, input')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.raiseTicket({
        kind,
        message,
        pageUrl: window.location.href,
        contactEmail: signedOut ? email : undefined,
        browserLogs: logs.length ? logs.slice(-20) : undefined,
      });
      setRaised({ id: res.id, deskRef: res.supportTicketNumber });
      setMessage('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('supportPill.couldNotReachServer'));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setOpen(false);
    setRaised(null);
    setError(null);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('supportPill.getHelp')}
        title={t('supportPill.getHelp')}
        className="fixed bottom-4 right-4 z-40 flex size-10 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg transition-colors hover:bg-brand-600 active:bg-brand-700"
      >
        <HelpCircle className="size-5" aria-hidden />
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('supportPill.support')}
      aria-modal="false"
      /* Height capped to the viewport, with the body scrolling rather than the
         whole panel, so Send stays reachable on a short window instead of being
         pushed off the bottom of the screen. */
      className="surface-card fixed bottom-4 right-4 z-40 flex max-h-[calc(100dvh-2rem)] w-[min(22rem,calc(100vw-2rem))] flex-col rounded-[var(--radius-card)] border line shadow-2xl"
    >
      <div className="flex shrink-0 items-center justify-between border-b line px-4 py-3">
        <h2 className="text-sm font-semibold text-strong">{raised ? t('supportPill.ticketRaised') : t('supportPill.getHelp')}</h2>
        <button
          type="button"
          onClick={reset}
          aria-label={t('supportPill.closeSupport')}
          className="rounded-md p-1 text-muted transition-colors hover:bg-[var(--surface-hover)] hover:text-strong"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      {raised ? (
        <div className="space-y-3 overflow-y-auto p-4">
          <p className="text-sm text-strong">
            {t('supportPill.thanksThisIsTicket')} <span className="font-semibold">#{raised.id}</span>
            {raised.deskRef && <> ({t('supportPill.supportRef', { ref: raised.deskRef })})</>}.
          </p>
          <p className="text-sm text-muted">
            {raised.deskRef
              ? t('supportPill.wellGetBackToYou')
              : // Be honest that the hand-off didn't complete. The report is
                // safely stored, but promising a reply we might not send is worse
                // than saying the desk was unreachable.
                t('supportPill.loggedDeskUnreachable')}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1 h-8" onClick={() => setRaised(null)}>
              {t('supportPill.raiseAnother')}
            </Button>
            <Button variant="primary" className="flex-1 h-8" onClick={reset}>
              {t('supportPill.done')}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3 overflow-y-auto p-4">
          {signedOut ? (
            <>
              <p className="text-sm text-muted">{t('supportPill.troubleSigningIn')}</p>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-strong">{t('supportPill.yourEmailAddress')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('supportPill.emailPlaceholder')}
                  className="surface-card w-full rounded-lg border line px-3 py-2 text-sm text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
            </>
          ) : (
            <label className="block">
              <span className="mb-1 block text-[13px] font-medium text-strong">{t('supportPill.whatDoYouNeedHelpWith')}</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as SupportKind)}
                className="surface-card w-full rounded-lg border line px-3 py-2 text-sm text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {KINDS().map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-strong">
              {kind === 'login_issue' ? t('supportPill.whatHappensSignIn') : t('supportPill.tellUsWhatHappened')}
            </span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={4}
              maxLength={5000}
              placeholder={
                kind === 'login_issue'
                  ? t('supportPill.loginIssuePlaceholder')
                  : t('supportPill.detailPlaceholder')
              }
              className="surface-card w-full resize-y rounded-lg border line px-3 py-2 text-sm text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
              {error}
            </p>
          )}

          <p className="text-[11px] text-muted">
            {t('supportPill.thePageYouAreOn')}
            {logs.length > 0 &&
              (logs.length === 1
                ? t('supportPill.oneRecentBrowserError', { n: logs.length })
                : t('supportPill.nRecentBrowserErrors', { n: logs.length }))}
            {t('supportPill.willBeSentToHelpDiagnose')}
          </p>

          <Button type="submit" variant="primary" loading={busy} className="w-full">
            {t('supportPill.send')}
          </Button>
        </form>
      )}
    </div>
  );
}

/**
 * Capture console errors and warnings so a ticket carries the diagnostics
 * instead of the reporter having to describe them.
 *
 * The patch is installed once on mount and always restores the ORIGINAL
 * functions on unmount, so remounting can't leave a chain of wrappers behind.
 * Entries are kept in a ref and mirrored to state only when the count changes,
 * because console.error can fire in a render path — setting state on every call
 * would risk a render loop on a page that is already erroring.
 */
function useCapturedLogs(): BrowserLog[] {
  const buffer = useRef<BrowserLog[]>([]);
  const [logs, setLogs] = useState<BrowserLog[]>([]);

  useEffect(() => {
    const MAX = 50;
    const original = { warn: console.warn, error: console.error };

    const wrap = (level: 'warn' | 'error') =>
      (...args: unknown[]) => {
        original[level](...args);
        try {
          const message = args
            .map((a) => {
              if (a instanceof Error) return `${a.name}: ${a.message}`;
              if (typeof a === 'object' && a !== null) {
                try {
                  return JSON.stringify(a);
                } catch {
                  return '[unserialisable]';
                }
              }
              return String(a);
            })
            .join(' ')
            .slice(0, 2000);

          if (IGNORED.some((re) => re.test(message))) return;

          buffer.current = [...buffer.current, { timestamp: new Date().toISOString(), level, message }].slice(-MAX);
          setLogs(buffer.current);
        } catch {
          // Capturing diagnostics must never itself break the page.
        }
      };

    console.warn = wrap('warn');
    console.error = wrap('error');
    return () => {
      console.warn = original.warn;
      console.error = original.error;
    };
  }, []);

  return logs;
}
